import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import getLeadOutreachThreads from "../http/getLeadOutreachThreads";
import getOutreachLatestTurn from "../http/getOutreachLatestTurn";
import getOutreachTrace from "../http/getOutreachTrace";
import postOutreachDraft, {
  OutreachDraftRequest,
} from "../http/postOutreachDraft";
import postOutreachPromptTest from "../http/postOutreachPromptTest";
import {
  deleteTaskPromptRule,
  getTaskPrompt,
  getTaskPromptDiff,
  reorderTaskPromptRules,
  restoreTaskPromptRevision,
  saveTaskPromptAddendum,
  saveTaskPromptPreamble,
  saveTaskPromptRule,
} from "../http/taskPrompt";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

// The outreach-email task on the unified `/leads/ai-prompt*` endpoint (mirrors the
// BE outreach.TASK_KEY). One editable rule set (there is no per-role split - the
// recipient's role is a fact in the prompt, not a separate persona).
const OUTREACH_TASK = "outreach_email";

// Draft (and re-draft) cold-outreach emails, plus the editable outreach-email prompt
// (rule set) CRUD + its test run. Mirrors the shape of useLeads / usePeopleDiscovery:
// the draft mutation holds its result in the caller's local state (the modal), so no
// cache invalidation; every prompt write invalidates the prompt query so the editor
// re-renders from the fresh active revision.
export const useOutreach = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const draftOutreach = useMutation(
    async ({ leadId, body }: { leadId: string; body: OutreachDraftRequest }) =>
      postOutreachDraft(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        body
      ),
    {
      // Refresh the per-contact thread summaries so the "v2" badge on the Draft email
      // button reflects the new turn (the modal keeps its own draft state).
      onSuccess: (_data, { leadId }) => {
        queryClient.invalidateQueries(["leadOutreachThreads", leadId]);
      },
    }
  );

  // Load an existing thread's latest saved turn (no draft / no AI call). Imperative, so
  // the modal can decide draft-vs-load on open and render the loaded turn just like a draft.
  const loadOutreachLatestTurn = useMutation(
    async ({
      leadId,
      outreachMessageId,
    }: {
      leadId: string;
      outreachMessageId: string;
    }) =>
      getOutreachLatestTurn(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        outreachMessageId
      )
  );

  const invalidateOutreachPrompt = {
    onSuccess: () => {
      queryClient.invalidateQueries(["outreachEmailPrompt"]);
    },
  };
  const saveOutreachPromptRuleMut = useMutation(
    async (body: Parameters<typeof saveTaskPromptRule>[2]) =>
      saveTaskPromptRule(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const deleteOutreachPromptRuleMut = useMutation(
    async (body: Parameters<typeof deleteTaskPromptRule>[2]) =>
      deleteTaskPromptRule(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const reorderOutreachPromptRulesMut = useMutation(
    async (body: Parameters<typeof reorderTaskPromptRules>[2]) =>
      reorderTaskPromptRules(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const saveOutreachPromptPreambleMut = useMutation(
    async (body: Parameters<typeof saveTaskPromptPreamble>[2]) =>
      saveTaskPromptPreamble(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const saveOutreachPromptAddendumMut = useMutation(
    async (body: Parameters<typeof saveTaskPromptAddendum>[2]) =>
      saveTaskPromptAddendum(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const restoreOutreachPromptRevisionMut = useMutation(
    async (body: Parameters<typeof restoreTaskPromptRevision>[2]) =>
      restoreTaskPromptRevision(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );

  const testOutreachPrompt = useMutation(
    async (body: Parameters<typeof postOutreachPromptTest>[1]) =>
      postOutreachPromptTest(await getAuthTokenBlocking(getToken, relogin), body)
  );

  return {
    draftOutreach,
    loadOutreachLatestTurn,
    saveOutreachPromptRuleMut,
    deleteOutreachPromptRuleMut,
    reorderOutreachPromptRulesMut,
    saveOutreachPromptPreambleMut,
    saveOutreachPromptAddendumMut,
    restoreOutreachPromptRevisionMut,
    testOutreachPrompt,
  };
};

// The stored AI trace for one outreach turn. `subjectRef` is the outreach message id
// (or a test run's synthetic ref); `runId` is the turn's traceRunId.
export const useOutreachTrace = (
  subjectRef: string | null,
  runId: string | null,
  enabled: boolean
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["outreachTrace", subjectRef, runId],
    async () =>
      getOutreachTrace(
        await getAuthTokenBlocking(getToken, relogin),
        subjectRef!,
        runId!
      ),
    { enabled: !!subjectRef && !!runId && enabled }
  );
};

// Existing outreach-draft threads for a lead (per role + contact, with the turn count),
// so each contact's Draft email button can show a "v2" badge when a thread already exists.
// One batched request per lead. Re-reads on mount so a just-created draft shows its badge.
export const useLeadOutreachThreads = (leadId: string, enabled: boolean) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadOutreachThreads", leadId],
    async () =>
      getLeadOutreachThreads(
        await getAuthTokenBlocking(getToken, relogin),
        leadId
      ),
    { enabled, staleTime: 0, refetchOnMount: "always" }
  );
};

// The editable outreach-email prompt: active revision + history + enforced footer.
// Admin-only (inrange-org gated).
export const useOutreachEmailPrompt = (enabled = true) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["outreachEmailPrompt"],
    async () =>
      getTaskPrompt(await getAuthTokenBlocking(getToken, relogin), OUTREACH_TASK),
    { enabled }
  );
};

// Rule-level + text diff between two revisions of the outreach-email prompt.
export const useOutreachEmailPromptDiff = (
  fromRevision: number | null,
  toRevision: number | null,
  enabled = true
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["outreachEmailPromptDiff", fromRevision, toRevision],
    async () =>
      getTaskPromptDiff(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        fromRevision!,
        toRevision!
      ),
    { enabled: enabled && fromRevision !== null && toRevision !== null }
  );
};

export default useOutreach;
